import { HistoryOutlined } from '@mui/icons-material';
import { Box, Grid, IconButton, Tooltip, Chip } from '@mui/material';
import PropTypes from 'prop-types';
import { useState } from 'react';
import OrderTicketsDropdown from '../../components/dashboard/home/OrderTicketsDropdown';
import ReviewColumnIcons from '../../components/dashboard/orders/ReviewColumnIcons';
import { renderColorBox } from '../../utils/cssStyles';
import { cloudinaryFull, cloudinaryThumbnail } from '../../utils/formatCloudinary';
import { fCurrency } from '../../utils/formatNumber';
import { fDate, fDateTime, fMonthDayYear } from '../../utils/formatTime';
import Iconify from '../iconify';
import Image from '../image';
import Lightbox from '../lightbox';
import OpenInNewTabField from './OpenInNewTabField';
import OrderLeftLabel from '../../components/dashboard/orders/OrderLeftLabel';

const renderIcon = (value, icon) => (
  <Box>
    <Iconify icon={icon} width={13} height={13} /> {value || ''}
  </Box>
);

const orderNumber = {
  field: 'orderNumber',
  headerName: 'PO #',
  width: 120,
  type: 'customSelect',
  valueGetter: (params) =>  [params.row.orderNumber, ...(params.row?.alternatePOs ?? [])].join(),
  customFilterOperators: ['hasAnyOf', 'containsAnySubstring'],
  getFilterValues: (row) => [row.orderNumber, ...(row?.alternatePOs ?? [])],
  renderCell: (params) => (
    <>
      <Tooltip
        title={
          params.row?.orderMessages?.length > 0 || params.row?.deleteFlag ? (
            <>
              {params.row?.deleteFlag && (
                <>
                  <Box>
                    <Box> Deleted {params.row.deletedBy && `by: ${params.row.deletedBy}`}</Box>
                    {params.row.dateDeleted && <Box>  On: {fMonthDayYear(params.row.dateDeleted)}</Box>}
                    {params.row.deletedReason && <div> Reason: {params.row.deletedReason} </div>}
                  </Box>
                  {params.row?.orderMessages?.length ? <br /> : null}
                </>
              )}
              {params.row?.orderMessages?.map((message) => (
                <div key={message}>
                  {message}
                </div>
              ))}
            </>
          ) : (
            ''
          )
        }
      >
        <Box
          sx={{
            color: params.row?.deleteFlag ? 'error.main' : params.row?.orderMessages?.length > 0 && 'info.main',
          }}
        >
          {params.row.orderNumber}
        </Box>
      </Tooltip>
    </>
  ),
};
const budgetOrderNumber = {
  ...orderNumber,
  renderCell: (params) => (
    <Box sx={{ color: 'info.main' }}>
      <OpenInNewTabField value={params.row.orderNumber} url={`/dashboard/orders/${params.row.orderId}?filter=previous`} />
    </Box>
  ),
};
const orderedFor = {
  field: 'orderedFor',
  headerName: 'Ordered For',
  minWidth: 120,
  flex: 1,
  type: 'customSelect',
};
const itemCount = {
  field: 'itemCount',
  headerName: 'Items',
  minWidth: 90,
  flex: 1,
  type: 'customNumber',
};

const orderedOn = {
  field: 'orderedOn',
  headerName: 'Ordered On',
  minWidth: 120,
  flex: 1,
  type: 'customDate',
  renderCell: (params) =>
    params.value ? renderIcon(fMonthDayYear(params.value), 'material-symbols:calendar-today-outline') : '',
  valueGetter: (params) => (params.value ? new Date(params.value) : null),
};
const department = {
  field: 'department',
  headerName: 'Category',
  minWidth: 110,
  flex: 0.75,
  renderCell: (params) => renderColorBox(params.value, 'secondary'),
  valueGetter: (params) => params.row.department,
  type: 'customSelect',
};
const qty = { field: 'itemCount', headerName: 'Qty', minWidth: 50, flex: 0.5 };
const quantity = {
  field: 'quantity',
  headerName: 'Qty',
  renderCell: (params) => params.value > 0 && params.value,
  flex: 0.5,
};

const dateAdded = {
  field: 'dateAdded',
  headerName: 'Submitted On',
  type: 'customDate',
  minWidth: 100,
  flex: 1.2,
  maxWidth: 220,
  renderCell: (params) =>
  renderIcon(`${fDateTime(params.row.dateAdded)} EST`, 'material-symbols:calendar-today-outline'),
  valueGetter: (params) => params.value ? new Date(params.value).getTime() : 0,
};
const group = {
  field: 'group',
  headerName: 'Group',
  type: 'customSelect',
  minWidth: 150,
  flex: 1.2,
};
const approvedOn = {
  field: 'dateApproved',
  headerName: 'Approved on',
  type: 'customDate',
  minWidth: 100,
  flex: 1,
  renderCell: (params) => params.value ? renderIcon(`${fDateTime(params.value)} EST`, 'material-symbols:calendar-today-outline') : 'Pending Order',
  valueGetter: (params) => (params.value ? new Date(params.value) : null),
};
const reviewers = {
  field: 'reviewers',
  headerName: 'Reviewed by',
  type: 'customSelect',
  minWidth: 100,
  flex: 1,
  renderCell: (params) => <ReviewColumnIcons params={params} />,
  valueGetter: (params) =>  params.row.reviews ? params.row.reviews?.join() : null,
  customFilterOperators: ['hasAnyOf', 'containsAnySubstring'],
  getFilterValues: (row) => row.reviews?.map((manager) => manager.manager),
};

const total = {
  field: 'total',
  headerName: 'Total',
  minWidth: 70,
  flex: 0.5,
  renderCell: (params) =>
    params.row.hasDeletedItems ? (
      <Tooltip
        title={
          <Box sx={{ textAlign: 'center' }}>
            This total has been adjusted
            <br />
            because an item was deleted
          </Box>
        }
        placement="top"
        arrow
      >
        <Box component="span">{fCurrency(params.value)}</Box>
      </Tooltip>
    ) : (
      fCurrency(params.value)
    ),
};

const vendors = {
  field: 'orderVendors',
  headerName: 'Vendors',
  minWidth: 120,
  flex: 0.8,
  type: 'customSelect',
  valueGetter: (params) => params.row.allvendors,
  sortComparator: (v1, v2, cellParams1, cellParams2) => {
    const row1 = cellParams1?.api?.getRow?.(cellParams1.id);
    const row2 = cellParams2?.api?.getRow?.(cellParams2.id);
    const a = (row1?.mainVendorInfo?.vendorName ?? '').toString();
    const b = (row2?.mainVendorInfo?.vendorName ?? '').toString();
    const aDeleted = !!row1?.deleteFlag;
    const bDeleted = !!row2?.deleteFlag;
    if (aDeleted && bDeleted) return 0;
    if (aDeleted || bDeleted) {
      const sortModel = cellParams1?.api?.getSortModel?.()
        ?? cellParams1?.api?.state?.sorting?.sortModel
        ?? [];
      const sortItem = sortModel.find((item) => item.field === cellParams1.field);
      const isDesc = sortItem?.sort === 'desc';
      if (aDeleted) return isDesc ? -1 : 1;
      return isDesc ? 1 : -1;
    }
    return a.localeCompare(b);
  },
  renderCell: (params) => {
    const subVendors = params.row.subvendorsInfo ?? [];
    const mainVendorInfo = params.row.mainVendorInfo;
    const inactiveSx = { color: 'text.disabled', fontStyle: 'italic' };
    const deletedVendorMessage = (
      <Box sx={{ textAlign: 'center' }}>
        The items from this vendor have
        <br />
        been deleted from this order
      </Box>
    );
    const mainVendorBox = (
      <Box
        sx={{
          mr: subVendors.length > 0 ? 1 : 0,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          flexShrink: 1,
          flexGrow: 1,
          ...(mainVendorInfo?.hasActiveItems === false ? inactiveSx : {}),
        }}
      >
        {mainVendorInfo?.vendorName}
      </Box>
    );
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
        {mainVendorInfo?.hasActiveItems === false ? (
          <Tooltip title={deletedVendorMessage} placement="top" arrow>
            {mainVendorBox}
          </Tooltip>
        ) : (
          mainVendorBox
        )}
        {subVendors.length > 0 && (
          <Tooltip
            title={
              <Box>
                {subVendors.map((vendor) => (
                  <Box key={vendor.vendorId} sx={vendor.hasActiveItems === false ? inactiveSx : {}}>
                    {vendor.vendorName}
                  </Box>
                ))}
              </Box>
            }
            placement="top"
            arrow
          >
            <Chip sx={{ height: 24, display: 'flex', alignItems: 'center' }} label={`+${subVendors.length}`} />
          </Tooltip>
        )}
      </Box>
    );
  },
  customFilterOperators: ['hasAnyOf', 'containsAnySubstring'],
  getFilterValues: (row) => row.allvendors ?? [],
};

const getHistoryTotal = (params) => params.row.quantityApproved * (params.row.originalPrice ?? 0) + (params.row.tax ?? 0) + (params.row.shipping ?? 0)

const budgetTotal = {
  field: 'total',
  headerName: 'Budget Total',
  minWidth: 70,
  flex: 0.5,
  renderCell: (params) => fCurrency(getHistoryTotal(params) || 0),
  valueGetter: (params) => getHistoryTotal(params) || 0,
};
const poTotal = {
  field: 'poTotal',
  headerName: 'PO Total',
  minWidth: 70,
  flex: 0.5,
  renderCell: (params) => fCurrency(params.value)
};

const description = {
  field: 'description',
  headerName: 'Description',
  width: 300,
  renderCell: (params) => (
    <Box
      component="div"
      sx={{
        fontSize: '0.7rem',
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 3,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'pre-wrap',
      }}
    >
      {params.value}
    </Box>
  ),
  type: 'customText',
};
const sentTo = {
  field: 'sentTo',
  headerName: 'Sent To',
  minWidth: 90,
  flex: 1,
  renderCell: (params) => (
    <Box
      component="div"
      sx={{
        wordBreak: 'break-all',
        whiteSpace: 'pre-wrap',
      }}
    >
      {params.value}
    </Box>
  ),
  type: 'customSelect',
};
const type = {
  field: 'type',
  headerName: 'Type',
  minWidth: 100,
  flex: 1,
  renderCell: (params) => (
    <Box
      component="div"
      sx={{
        wordWrap: 'break-word',
        whiteSpace: 'pre-wrap',
      }}
    >
      {params.value}
    </Box>
  ),
  type: 'customText',
};

const timeStamp = {
  field: 'timeSent',
  headerName: 'Sent on',
  flex: 0.8,
  renderCell: (params) => renderIcon(fDateTime(params.value), 'material-symbols:calendar-today-outline'),
  valueGetter: (params) => new Date(params.value),
  type: 'customDate',
};

const descriptionWide = {
  field: 'description',
  headerName: 'Description',
  minWidth: 100,
  flex: 2,
  renderCell: (params) => (
    <Box
      component="div"
      sx={{
        fontSize: '0.7rem',
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 3,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'pre-wrap',
      }}
    >
      {params.value}
    </Box>
  ),
  type: 'customSelect',
};

const facilityName = {
  field: 'facilityName',
  headerName: 'Facility',
  minWidth: 110,
  flex: 0.75,
  type: 'customSelect',
  renderCell: (params) => <>{params.row.facilityName}</>,
};
const facility = {
  field: 'facility',
  headerName: 'Facility',
  minWidth: 90,
  flex: 0.75,
  type: 'customSelect',
};
const personFor = {
  field: 'personFor',
  headerName: 'On Behalf Of',
  minWidth: 80,
  flex: 1,
  type: 'customSelect',
};
const vendorShipDate = {
  field: 'vendorShipDate',
  headerName: 'Ship Day',
  headerAlign: 'left',
  minWidth: 90,
  flex: 1,
  renderCell: (params) => (
    <Tooltip
      title={
        (params.row.requestedShipDate && params.row.vendorShipDate && params.row.vendorCutoff && (
          <div>
            <div>Original ship date</div>
            <div>{params.row.vendorShipDate}</div>
            <div>{params.row.vendorCutoff} </div>
          </div>
        )) ||
        (params.row.requestedShipDate && params.row.vendorShipDate && (
          <div>
            <div>Original ship date</div>
            <div>{params.row.vendorShipDate}</div>
          </div>
        )) ||
        (params.row.vendorCutoff && <div>{params.row.vendorCutoff}</div>)
      }
      placement="top"
      arrow
    >
      <Box
        component="div"
        sx={{
          fontSize: '0.75rem',
          wordWrap: 'break-word',
          whiteSpace: 'pre-wrap',
          color: params.row.vendorCutoff && 'info.main',
        }}
      >
        {params.row.requestedShipDate ? `Requested | ${params.row.requestedShipDate}` : params.row.vendorShipDate}
        {params.row.requestedShipDate && params.row.vendorShipDate && (
          <Box sx={{ color: 'info.main', display: 'inline' }}>
            <Iconify
              icon="material-symbols:info-outline"
              width={18}
              height={18}
              style={{ paddingTop: '5px', paddingLeft: '2px' }}
            />
          </Box>
        )}
      </Box>
    </Tooltip>
  ),
  valueGetter: (params) => params.row.vendorShipDate,
  type: 'customText',
};
const submittedBy = {
  field: 'submittedBy',
  headerName: 'Submitted By',
  minWidth: 120,
  flex: 1,
  type: 'customSelect',
};
export default function RenderImage({ params }) {
  RenderImage.propTypes = {
    params: PropTypes.object,
  };
  const { pictures } = params.row;
  const picture = pictures[0];
  const [selectedImage, setSelectedImage] = useState(-1);
  const handleOpenLightbox = () => {
    setSelectedImage(0);
  };
  const handleCloseLightbox = () => {
    setSelectedImage(-1);
  };
  return (
    <>
      <Image
        alt="attachment"
        src={
          pictures?.length
            ? cloudinaryThumbnail(picture)
            : cloudinaryThumbnail(
              'https://res.cloudinary.com/agoraserv/image/upload/v1738258682/No%20Image%20Animation.gif'
            )
        }
        sx={{
          cursor: 'pointer',
          maxWidth: '45px',
          maxHeight: '45px',
          '&:hover': {
            opacity: 0.9,
          },
        }}
        onClick={() => handleOpenLightbox()}
      />
      <Lightbox
        index={0}
        slides={pictures?.map((picture) => ({ src: cloudinaryFull(picture) }))}
        open={selectedImage >= 0}
        close={handleCloseLightbox}
      />
    </>
  );
}

const picture = {
  field: 'picture',
  headerName: 'Image',
  flex: 0.4,
  renderCell: (params) => <RenderImage params={params} />,
  sortable: false,
  filterable: false,
};
const ordered = {
  field: 'ordered',
  headerName: 'Ordered/Approved',
  minWidth: 90,
  flex: 1,
  valueGetter: (params) => `${params.row.quantityOrdered} | ${params.row.quantityApproved}`,
  renderCell: (params) => params.value,
  getFilterValues: (row) => [`${row.quantityOrdered} | ${row.quantityApproved}`],
  type: 'customSelect',
};
const originalPrice = {
  field: 'originalPrice',
  headerName: 'Price',
  minWidth: 80,
  flex: 1,
  renderCell: (params) => fCurrency(params.value),
};
const tax = {
  field: 'tax',
  headerName: 'Tax',
  minWidth: 80,
  flex: 1,
  renderCell: (params) => fCurrency(params.value),
};
const shipping = {
  field: 'shipping',
  headerName: 'Shipping',
  minWidth: 80,
  flex: 1,
  renderCell: (params) => fCurrency(params.value),
};

const status = {
  field: 'status',
  headerName: 'Status',
  minWidth: 90,
  flex: 1,
  type: 'customSelect',
};
const agoraSubcategory = {
  field: 'agoraSubcategory',
  headerName: 'Subcategory',
  minWidth: 90,
  flex: 1,
  type: 'customSelect',
};
const agoraCategory = {
  field: 'agoraCategory',
  headerName: 'Category',
  minWidth: 90,
  flex: 1,
  type: 'customSelect',
};
const vendor = {
  field: 'vendor',
  headerName: 'Vendor',
  minWidth: 90,
  flex: 1,
  type: 'customSelect'
};

const uom = {
  field: 'uom',
  headerName: 'UOM',
  valueGetter: (params) => `${params.row.uomAmount ? params.row.uomAmount : ''}/${params.row.uomType ? params.row.uomType : ''}`,
  renderCell: (params) => params.value,
  getFilterValues: (row) => [`${row.uomAmount ? row.uomAmount : ''}/${row.uomType ? row.uomType : ''}`],
  minWidth: 60,
  flex: 0.4,
  type: 'customSelect',
};

const statusCode = {
  field: 'statusCode',
  flex: 1.5,
  headerName: 'Status',
  renderCell: (params) => (
    <Grid container direction="row" justifyContent="center" alignItems="center">
      <Grid item>
        {' '}
        {params.row?.statusUpdates?.length > 0 && (
          <Tooltip
            title={
              params.row?.statusUpdates?.length > 0
                ? params.row?.statusUpdates?.map((status) => (
                  <div key={status}>
                    <div>Status: {status.status}</div>
                    <div>Date: {fMonthDayYear(status.dateAdded)}</div>
                    <div>Updated by: {status.updatedBy}</div>
                    <br />
                    <br />
                  </div>
                ))
                : ''
            }
          >
            <Iconify color="info" icon="material-symbols:info-outline" width={16} height={16} />
          </Tooltip>
        )}
      </Grid>
      <Grid item>
        <Grid container direction="column" justifyContent="center" alignItems="center">
          <Box
            component="div"
            sx={{
              wordWrap: 'break-word',
              whiteSpace: 'pre-wrap',
            }}
          >
            {params.value}
          </Box>
        </Grid>
      </Grid>
    </Grid>
  ),
};
const tickets = {
  field: 'ticketIds',
  headerName: 'Ticket #',
  flex: 1,
  minWidth: 80,
  type: 'customSelect',
  renderCell: (params) => <OrderTicketsDropdown params={params} />,
  customFilterOperators: ['hasAnyOf', 'containsAnySubstring'],
  getFilterValues: (row) => row.ticketIds ?? [],
};

const orderDelay = {
  field: 'orderDelay',
  headerName: 'Order Delay',
  flex: 1,
  renderCell: (params) => {
    const delay = params.row.orderDelay;

    if (!delay) return <></>;

    const sendDate = new Date(delay.sendOrderAt);
    const isPast = sendDate <= new Date();

    return (
      <Tooltip
        title={
          <div>
            Order was set on a timer by {delay.addedBy} on{' '}
            {fDate(delay.dateAdded, 'MM/dd/yyyy')}
            <br />
            Order {isPast ? 'was' : 'will be'} sent out on
            {fDate(delay.sendOrderAt, 'MM/dd/yyyy hh:mm a')}
          </div>
        }
        placement="top"
      >
        <IconButton>
          <HistoryOutlined color="warning" />
        </IconButton>
      </Tooltip>
    );
  },
};

const label = {
    field: 'label',
    headerName: '',
    minWidth: 15,
    resizable: false,
    maxWidth: 15,
    headerAlign: 'center',
    cellClassName: () => 'no-x-padding',
    headerClassName: () => 'no-x-padding no-resizable',
    renderCell: (params) => <OrderLeftLabel params={params} />,
    valueGetter: (params) => (params.row.read === false ? 'unresponded' : 'responded'),
};

const PENDING_ORDERS = [
  label,
  orderNumber,
  facilityName,
  group,
  vendorShipDate,
  department,
  personFor,
  dateAdded,
  submittedBy,
  qty,
  total,
  tickets,
];
const COMPLETED_ORDERS = [
  orderNumber,
  facility,
  group,
  department,
  personFor,
  dateAdded,
  submittedBy,
  orderDelay,
  approvedOn,
  reviewers,
  qty,
  total,
  vendors
];
const COMPLETED_ORDERS_BY_VENDOR = [
    orderNumber,
    facility,
    group,
    vendor,
    department,
    personFor,
    dateAdded,
    submittedBy,
    orderDelay,
    approvedOn,
    reviewers,
    qty,
    total,
];
const TICKET_ORDERS = [description, quantity, statusCode];
const COPY_ORDER = [orderNumber, orderedFor, orderedOn, itemCount];
const VIEW_ORDER_HISTORY = [sentTo, type, timeStamp];
const BUDGET_HISTORY = [
  picture,
  budgetOrderNumber,
  descriptionWide,
  vendor,
  uom,
  orderedOn,
  approvedOn,
  ordered,
  originalPrice,
  tax,
  shipping,
  poTotal,
  budgetTotal,
  status,
  agoraCategory,
  agoraSubcategory
];
export { BUDGET_HISTORY, COMPLETED_ORDERS, COMPLETED_ORDERS_BY_VENDOR, COPY_ORDER, PENDING_ORDERS, TICKET_ORDERS, VIEW_ORDER_HISTORY };
